import React from 'react';
import NewBadge from '../../components/NewBadge';
import { syncNewTheme, saveNewTheme, exportNewTheme, importNewTheme, clearExportUrls, resetErrors } from '../../modules/new_theme'
import withRouter from 'with-router'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'

import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import ItemsList from '../../components/ItemsList'
import ItemsListSkeleton from '../../components/ItemsListSkeleton'
import LoadingIndicator from '../../components/LoadingIndicator';
import EmptyView from '../../components/EmptyView';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
// the sidebar's icons for the same resources, so a template is recognised at a glance
import { SECTION_ICONS } from '../../sectionIcons';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCartOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import WebOutlinedIcon from '@mui/icons-material/WebOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Link from '@mui/material/Link';
import { Trans, withTranslation, useTranslation } from 'react-i18next';

import OptionsMenu from '../../components/OptionsMenu';
import SolutionsArticleLink from '../../components/SolutionsArticleLink';
import ConditionalQuerySection from '../../components/ConditionalQuerySection';
import ExportImportDialogs from '../../components/ExportImportDialogs';
import API from '../../api/api';


// One item per first path segment ("general", "sections", "section", ...). The "section" group
// (singular) is the theme editor content, which Shopify keeps apart from the theme's locale
// files - it leads, in a card of its own. With the split switch on it is replaced by one item
// per template file: Shopify's keys read section.<template>.json.<section id>.<setting>, and
// a custom template carries its type in its name ("page.contact", "collection.best-seller").
// Not to be confused with the "sections" locale namespace
const templateOf = (parts) => {
  const marker = parts.indexOf('json');
  if(marker > 1) return parts.slice(1, marker).join('.');
  return parts.length > 2 ? parts[1] : null;
}

// the item a theme string's path belongs to; the search links its results with it
export const themeItemOf = (path, splitEditorContent) => {
  const parts = path.split('.');
  const group = parts[0];
  const subsection = (group === 'section' && splitEditorContent) ? templateOf(parts) : null;
  // item ids end up in DOM ids and in the item's URL, so anything but letters, digits and
  // dashes is folded into a dash (a section group's key carries a slash)
  return { itemId: subsection !== null ? `section-${subsection.replace(/[^A-Za-z0-9-]+/g, '-')}` : group, group, subsection };
}

export const THEME_SPLIT_STORAGE_KEY = 'ly_themeSectionFilter';

class NewTheme extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      strings: [],
      showDownloadDialog: true,
      showImportCompleteDialog: false,
      errorLockCollapse: false,
      forceOpenExportDialog: false,
      forceOpenImportDialog: false,
      // theme editor content as one item per theme section instead of one "Section" item
      sectionFilter: localStorage.getItem(THEME_SPLIT_STORAGE_KEY) === 'true',
      // items with unsaved edits; the split switch remounts the list, which would drop them
      unsavedCount: 0,
    }
  }

  onToggleSectionFilter = (event) => {
    const sectionFilter = event.target.checked;
    localStorage.setItem(THEME_SPLIT_STORAGE_KEY, String(sectionFilter));
    this.setState({ sectionFilter, strings: this.buildItems(this.props.new_theme.strings, sectionFilter) });
  }

  componentDidMount() {
    this.props.syncNewTheme(this.props.themes.edit_theme, this.props.editLanguage)
    if(this.props.new_theme.export_po_download_url || this.props.new_theme.export_csv_download_url) {
      this.setState({showDownloadDialog: false})
    }
  }

  componentWillUnmount() {
    API.clearAllFetches();
  }

  componentWillReceiveProps(newProps) {
    const currentLanguage = this.props.editLanguage;
    const newLanguage = newProps.editLanguage;
    const currentTheme = this.props.themes.edit_theme;
    const newTheme = newProps.themes.edit_theme;
    if(currentLanguage != newLanguage || currentTheme != newTheme) {
      this.props.syncNewTheme(newTheme, newLanguage)
      return
    }

    if(this.props.new_theme.import_new_theme_pending === true && newProps.new_theme.import_new_theme_pending === false && !newProps.new_theme.error && !newProps.new_theme.import_cancelled) {
      this.setState({showImportCompleteDialog: true}, () => {
        this.setState({showImportCompleteDialog: false});
      })
    }

    if((this.props.new_theme.sync_new_theme_pending === true && newProps.new_theme.sync_new_theme_pending === false) 
      || (this.props.new_theme.save_new_theme_pending === true && newProps.new_theme.save_new_theme_pending === false)) {
      this.setState({ strings: this.buildItems(newProps.new_theme.strings, this.state.sectionFilter) });
    }
  }

  // the theme editor's names: "index" is the home page, "product" the default product template,
  // a custom "product.bundle" reads "Product · bundle". Unknown templates keep their key
  templateTitle = (template) => {
    const { t } = this.props;
    const defaults = ['index', 'product', 'collection', 'page', 'blog', 'article', 'cart', 'search', '404', 'password', 'gift_card', 'list-collections'];
    if(defaults.includes(template)) return t(`pages.theme.templates.${template}`);
    // section groups (header, footer, ...) live in sections/<name>-group.json
    if(template.startsWith('sections/')) {
      const name = template.slice('sections/'.length).replace(/-group$/, '').replace(/-/g, ' ');
      return t('pages.theme.templates.sectionGroup', { name: name.charAt(0).toUpperCase() + name.slice(1) });
    }
    const dot = template.indexOf('.');
    const type = dot > 0 ? template.slice(0, dot) : null;
    if(type && ['product', 'collection', 'page', 'blog', 'article'].includes(type)) {
      return `${t(`pages.theme.templateTypes.${type}`)} · ${template.slice(dot + 1)}`;
    }
    return template;
  }

  templateIcon = (template) => {
    if(template.startsWith('sections/')) return <WebOutlinedIcon fontSize="small" />;
    const type = template.split('.')[0];
    const icons = {
      index: <HomeOutlinedIcon fontSize="small" />,
      product: <SECTION_ICONS.products fontSize="small" />,
      collection: <SECTION_ICONS.collections fontSize="small" />,
      'list-collections': <SECTION_ICONS.collections fontSize="small" />,
      page: <SECTION_ICONS.pages fontSize="small" />,
      blog: <SECTION_ICONS.blogs fontSize="small" />,
      article: <SECTION_ICONS.articles fontSize="small" />,
      cart: <ShoppingCartIcon fontSize="small" />,
      search: <SearchOutlinedIcon fontSize="small" />,
      gift_card: <CardGiftcardOutlinedIcon fontSize="small" />,
      password: <LockOutlinedIcon fontSize="small" />,
      '404': <ErrorOutlineOutlinedIcon fontSize="small" />,
    };
    // a template of an unknown type still gets a file icon so the rows line up
    return icons[type] || <InsertDriveFileOutlinedIcon fontSize="small" />;
  }

  // "product.bundle" is a custom template of the "product" type; the default template carries
  // the bare type. Section groups and unknown names count as their own type
  templateType = (template) => {
    if(!template || template.startsWith('sections/')) return template;
    return template.split('.')[0];
  }

  buildItems = (strings, splitEditorContent) => {
    const order = [];
    const fieldsByItem = {};
    const translatedByItem = {};
    const titles = {};
    const templates = {};

    strings.forEach(string => {
      const { itemId, group, subsection } = themeItemOf(string.path, splitEditorContent);
      if(!fieldsByItem[itemId]) {
        order.push(itemId);
        fieldsByItem[itemId] = [];
        translatedByItem[itemId] = 0;
        titles[itemId] = subsection !== null ? this.templateTitle(subsection) : group.charAt(0).toUpperCase() + group.slice(1);
        templates[itemId] = subsection;
      }
      if(string.target) translatedByItem[itemId] += 1;

      fieldsByItem[itemId].push({
        id: string.id,
        multiline: true,
        rows: 5,
        type: 'text',
        label: string.path || '',
        source: string.source || '',
        target: string.target || '',
        outdated: (typeof string.outdated === 'boolean') ? string.outdated : null,
      });
    });

    const isEditorItem = (itemId) => itemId === 'section' || itemId.startsWith('section-');
    const items = order.map(itemId => ({
      type: 'theme___' + this.props.themes.edit_theme,
      id: itemId,
      synchronized: false,
      title: titles[itemId],
      icon: templates[itemId] ? this.templateIcon(templates[itemId]) : null,
      // custom templates sit indented under their type's default template
      indent: !!templates[itemId] && this.templateType(templates[itemId]) !== templates[itemId],
      info: '',
      groupId: isEditorItem(itemId) ? 'editor' : 'locale',
      groupLabel: this.props.t(isEditorItem(itemId) ? 'pages.theme.groups.sections' : 'pages.theme.groups.locale'),
      groupInfo: this.props.t(isEditorItem(itemId) ? 'pages.theme.groups.sectionsInfo' : 'pages.theme.groups.localeInfo'),
      fields: fieldsByItem[itemId],
      progress: fieldsByItem[itemId].length > 0 ? (translatedByItem[itemId] / fieldsByItem[itemId].length) * 100 : 100,
    }));
    // editor content first, then the locale groups (by key). Templates sort by the type's displayed
    // name in the app language - by key the German list read Artikel, Blog, Kollektion, Startseite -
    // the default template ("product") ahead of its custom ones, those by their displayed title
    const language = this.props.i18n?.language;
    const compare = (a, b) => String(a).localeCompare(String(b), language, { sensitivity: 'base' });
    // a type with custom templates sorts under its plain name ("Produkt"), not under the default
    // template's title ("Standard-Produkt"), so the group lands where the merchant looks for it
    const typeTitle = (type) => (['product', 'collection', 'page', 'blog', 'article'].includes(type)
      ? this.props.t(`pages.theme.templateTypes.${type}`)
      : this.templateTitle(type));
    const sortKey = (item) => {
      const template = templates[item.id];
      if(!template) return [item.id, 0, ''];
      const type = this.templateType(template);
      return [typeTitle(type), template === type ? 0 : 1, item.title];
    };
    return items.sort((a, b) => {
      const ea = isEditorItem(a.id), eb = isEditorItem(b.id);
      if(ea !== eb) return ea ? -1 : 1;
      const ka = sortKey(a), kb = sortKey(b);
      return compare(ka[0], kb[0]) || (ka[1] - kb[1]) || compare(ka[2], kb[2]);
    });
  }

  // confirmedFieldIds: fields a machine translation run just wrote (or "keep translation" named);
  // selectedFields: the list's pending edits, keyed `${itemId}-${fieldId}` - a per-field machine
  // translation registers there. Both go out even when the text equals the stored translation:
  // the result is a fresh translation all the same, and Shopify's outdated flag only clears on a
  // write (without this the field showed the unsaved bar and the save had nothing to send)
  onSave = (items, selectedFields, pinnedLanguage, confirmedFieldIds = []) => {
    const confirmed = new Set((confirmedFieldIds || []).map(String));
    const pending = (item, field) => Object.prototype.hasOwnProperty.call(selectedFields || {}, `${item.id}-${field.id}`);
    const usedItems = []
    items.map(item => {
      item.fields.forEach(field => {
        const stored = this.props.new_theme.strings.find(x => x.id === field.id);
        if(confirmed.has(String(field.id)) || pending(item, field) || field.target !== ((stored && stored.target) || '')) {
          usedItems.push({
            id: field.id,
            text: field.target
          });         
        }
      });
    });
    this.props.saveNewTheme(this.props.themes.edit_theme, pinnedLanguage || this.props.editLanguage, usedItems)
  }

  onResaveAll = (itemId) => {
    const item = this.state.strings.find(x => x.id == itemId);
    if(item) {
      const usedItems = [];
      item.fields.forEach(field => {
        usedItems.push({
          id: field.id,
          text: field.target
        });
      })

      this.props.saveNewTheme(this.props.themes.edit_theme, this.props.editLanguage, usedItems)
    }
  }

  onImport = (e) => {
    this.setState({forceOpenImportDialog: true}, () => {
      this.setState({forceOpenImportDialog: false});
    });   
    this.setState({ optionsMenuAnchorEl: null });
  }

  onExport = (e) => {
    this.setState({forceOpenExportDialog: true}, () => {
      this.setState({forceOpenExportDialog: false});
    });   
  }

  render() {
    const { t } = this.props

    if(!this.props.themes.edit_theme || !this.props.themes.themes.find(x => x.id == this.props.themes.edit_theme)) {
      return <EmptyView
        title={t('pages.theme.noThemeTitle')}
        description={t('pages.theme.noThemeDescription')}
      />
    }

    if(this.props.new_theme.sync_new_theme_pending === true || (this.props.new_theme.error && this.props.new_theme.error.level === 0)) {
      return <React.Fragment>
        <Typography variant="h4" gutterBottom component="h2">
          {t('pages.theme.title')} - {this.props.themes.themes.find(x => x.id == this.props.themes.edit_theme).name}
        </Typography>
        <Divider className="divider-white" />
        <ItemsListSkeleton rows="10" hideBottomToolbar/>
        <LoadingIndicator
          pending={this.props.new_theme.sync_new_theme_pending}
          progress={this.props.new_theme.progress}
          error={this.props.new_theme.error}
        />

      </React.Fragment>  
    }

    return <React.Fragment>
      <Typography variant="h4" gutterBottom component="h2">
        {t('pages.theme.title')} - {this.props.themes.themes.find(x => x.id == this.props.themes.edit_theme).name}

        <OptionsMenu
          dataTour="page-options-menu"
          onImport={this.onImport}
          onExport={this.onExport}
          shopPlan={this.props.shopPlan}
        />

        <SolutionsArticleLink
          article="homepageTranslations"
          subject={t('pages.theme.title')}
          placement="headingMenu"
        />
      </Typography>

      <Divider className="divider-white" />

      <ConditionalQuerySection languages={this.props.languages.languages} />

      <ItemsList
        // the list adopts new items only around a save or sync; the split rebuilds them, so it remounts
        key={`theme-items-${this.state.sectionFilter}`}
        onUnsavedChange={count => this.setState({ unsavedCount: count })}
        virtualized={false}
        type="theme"
        // a theme string's label is its key, which is the useful thing to search by
        searchFieldLabels={true}
        // sorting the files themselves was not useful, so the entry sort replaces it
        hideSorting={true}
        sortFields={true}
        pagination={false}
        paginate={false}
        isPending={
          this.props.new_theme.sync_new_theme_pending ||
          this.props.new_theme.export_new_theme_pending ||
          this.props.new_theme.import_new_theme_pending ||
          this.props.new_theme.save_new_theme_pending
        }
        isSaving={
          this.props.new_theme.save_new_theme_pending
        }
        data={this.state.strings}
        onSave={this.onSave}
        onResaveAll={this.onResaveAll}
        extraRouteSegment={this.props.themes.edit_theme}
        toolbarTitle="Additional Fields"
        allowAutoTranslation={true}
        allowFieldFiltering={true}
        groupActions={{
          editor: <Tooltip title={this.state.unsavedCount > 0 ? t('pages.theme.sectionFilterLocked') : ''} arrow>
            <FormControlLabel
              style={{ marginRight: 0 }}
              disabled={this.state.unsavedCount > 0}
              control={<Switch size="small" checked={this.state.sectionFilter} onChange={this.onToggleSectionFilter} />}
              label={<Typography variant="body2" component="span">{t('pages.theme.sectionFilter')} <NewBadge sx={{ ml: 0.5, verticalAlign: 'middle' }} /></Typography>}
            />
          </Tooltip>
        }}
        error={this.props.new_theme.error}
        errorLockCollapse={this.state.errorLockCollapse}
        // the item menu offers "show markup problems"; fields warn about broken markup (theme only)
        markupCheck
      />
      <LoadingIndicator
        pending={
          this.props.new_theme.sync_new_theme_pending 
          || this.props.new_theme.save_new_theme_pending 
          || this.props.new_theme.import_new_theme_pending 
          || this.props.new_theme.export_new_theme_pending
        }
        loadingType={this.props.new_theme.save_new_theme_pending ? 'saving' : 'loading'}
        progress={this.props.new_theme.progress}
        error={this.props.new_theme.error}
        onErrorCancel={this.props.resetErrors}
        onErrorRetry={() => {
          this.setState({ errorLockCollapse: true }, () => {
            this.setState({ errorLockCollapse: false });
          });
        }}
      />

      <ExportImportDialogs
        //showDownloadDialog={this.state.showDownloadDialog}
        showImportCompleteDialog={this.state.showImportCompleteDialog}
        imported_entries={this.props.new_theme.imported_entries}
        export_po_download_url={this.props.new_theme.export_po_download_url}
        export_csv_download_url={this.props.new_theme.export_csv_download_url}
        export_advanced_csv_download_url={this.props.new_theme.export_advanced_csv_download_url}
        forceOpenExportDialog={this.state.forceOpenExportDialog}
        forceOpenImportDialog={this.state.forceOpenImportDialog}
        exportFunc={(options) => { this.props.exportNewTheme(this.props.themes.edit_theme ,this.props.editLanguage, options) }}
        importFunc={(formData, fileType, options) => { this.props.importNewTheme(formData, fileType, this.props.themes.edit_theme, this.props.editLanguage) }}
        syncFunc={() => { this.props.syncNewTheme(this.props.themes.edit_theme ,this.props.editLanguage)}}
        clearFunc={this.props.clearExportUrls}
        pending={this.props.new_theme.export_new_theme_pending}
        scope={'theme'}
        additionalFields={['with_BOM']}
      />
    </React.Fragment> 
  }
}

const mapStateToProps = ({ shop, themes, new_theme, languages }) => ({
  shopPlan: shop.shop.charge.name,
  themes: themes,
  new_theme: new_theme,
  editLanguage: shop.shop.edit_language,
  languages: languages
})

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      syncNewTheme,
      saveNewTheme,
      exportNewTheme,
      importNewTheme,
      resetErrors,
      clearExportUrls
    },
    dispatch
  )

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(NewTheme)))